import gql from 'graphql-tag';
import { ref } from 'vue';
import apolloClient from '@/apollo/client';

import type { AppointmentEntryAttributes, Availability } from '@/types';

// The batch version of the `availability` query (see ./availabilities.ts).
// It checks the availability for every entry in the series in a single request,
// returning an array of availabilities positionally matching the entries.

const query = gql`
  query checkAvailabilities(
    $entries: [AppointmentEntryAttributes!]!
    $locationId: ID
  ) {
    availabilities(entries: $entries, locationId: $locationId) {
      available
      appointmentPartAvailabilities {
        partId
        availableResourceIds
      }
    }
  }
`;

export const useSeriesAvailabilities = () => {
  const isFetchingAvailabilities = ref(false);

  // The results, positionally aligned with the entries passed to fetchAvailabilities.
  const availabilities = ref<Availability[]>([]);

  let debounceTimeout: ReturnType<typeof setTimeout>;

  const fetchAvailabilities = (
    entries: AppointmentEntryAttributes[],
    locationId: number | null,
    debounce = 0,
  ) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    isFetchingAvailabilities.value = true;

    return new Promise<Availability[]>((resolve) => {
      debounceTimeout = setTimeout(() => {
        if (!entries.length) {
          availabilities.value = [];
          isFetchingAvailabilities.value = false;
          resolve([]);
          return;
        }

        apolloClient
          .query({
            query,
            variables: { entries, locationId: locationId?.toString() },
            // Availability is time-sensitive, so we never want a cached result.
            fetchPolicy: 'network-only',
          })
          .then(({ data: { availabilities: result } }) => {
            availabilities.value = result;
            isFetchingAvailabilities.value = false;
            resolve(result);
          })
          .catch(() => {
            isFetchingAvailabilities.value = false;
            resolve([]);
          });
      }, debounce);
    });
  };

  const resetAvailabilities = () => {
    availabilities.value = [];
  };

  return {
    isFetchingAvailabilities,
    availabilities,
    fetchAvailabilities,
    resetAvailabilities,
  };
};
